import { Select, type SelectProps } from "@mantine/core";
import { ChevronDown } from "react-feather";
import clsx from "clsx";
import {
  FieldValues,
  UseControllerProps,
  useController,
} from "react-hook-form";

export type TSelectProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<SelectProps, "value" | "defaultValue">;

const TSelect = <T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  classNames,
  className,
  shouldUnregister,
  onChange,
  ...props
}: TSelectProps<T>) => {
  const {
    field: { value, onChange: fieldOnChange, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });
  return (
    <div className={clsx("w-full", className)}>
      <Select
        radius="xs"
        classNames={{
          input: clsx(
            "h-[50px] w-full border-b border-b-primary-500 bg-primary-50 px-4 py-4 pr-8 text-sm text-black-900 placeholder-gray-500 focus:border-b-primary-900",
            fieldState.error?.message && "focus:!border focus:!border-red-500",
          ),
          error: "text-red-500",
          ...classNames,
        }}
        variant="unstyled"
        withErrorStyles
        rightSection={<ChevronDown size={20} className="text-gray-500" />}
        value={value}
        onChange={(v, o) => {
          fieldOnChange(v);
          onChange?.(v, o);
        }}
        searchable
        {...field}
        {...props}
      />
      {fieldState.error?.message && (
        <span className="mt-1 text-sm leading-3 text-red-500">
          {fieldState.error?.message}
        </span>
      )}
    </div>
  );
};

export default TSelect;
